import React, {useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import {Box, Button, Card, CardContent, Typography, Avatar, Divider, Container, Chip} from '@mui/material';
import authenticationManager from '../../Auth/AuthenticationManager';
import UserApiService from '../../api/UserApiService';
import PropTypes from 'prop-types';
import EditUserModal from '../Modal/User/EditUserModal';
import {useNavigate} from 'react-router-dom';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Home as HomeIcon,
    LocationCity as CityIcon,
    CreditCard as LicenseIcon,
    Edit as EditIcon
} from '@mui/icons-material';

export default function UserInfo(props) {
    const { setSnackbarProperties } = props;
    const [user, setUser] = useState({});
    const [showEditModal, setShowEditModal] = useState(false);

    const userApiService = new UserApiService();

    const navigate = useNavigate();

    async function getUserInfo(userId) {
        const userInfo = await userApiService.getUserInfo(userId);

        if(userInfo.success) {
            setUser(userInfo.data);
        } else {
            setSnackbarProperties('Unable to retrieve user info', 'error');
        }
    }

    useEffect(() => {
        if(!authenticationManager.isAuthorized) {
            navigate('/login');
        }
        document.title = 'Executive Storage Suites - Your Info';
        const userId = authenticationManager.getUserId();
        getUserInfo(userId);
    }, [showEditModal]);

    const getUserInitials = () => {
        if (user.firstName && user.lastName) {
            return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
        }
        return user.firstName ? user.firstName.charAt(0) : 'U';
    };

    const InfoItem = ({ icon, label, value, span = false }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mr: 2, 
                color: '#007A03',
                minWidth: span ? 'auto' : 140
            }}>
                {icon}
                <Typography 
                    variant="body2" 
                    sx={{ 
                        ml: 1, 
                        fontWeight: 600, 
                        color: '#2c3e50',
                        fontSize: '0.9rem'
                    }}
                >
                    {label}:
                </Typography>
            </Box>
            <Typography 
                variant="body1" 
                sx={{ 
                    color: '#34495e',
                    fontSize: '1rem',
                    flex: 1,
                    wordBreak: span ? 'break-all' : 'normal'
                }}
            >
                {value || 'Not provided'}
            </Typography>
        </Box>
    );

    InfoItem.propTypes = {
        icon: PropTypes.element.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string,
        span: PropTypes.bool,
    };

    return(
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Profile Header */}
            <Card 
                elevation={8}
                sx={{ 
                    mb: 4, 
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 3 }}>
                        <Avatar
                            sx={{
                                width: 80,
                                height: 80,
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                background: 'linear-gradient(135deg, #007A03 0%, #005502 100%)',
                                boxShadow: '0 8px 32px rgba(0, 122, 3, 0.3)'
                            }}
                        >
                            {getUserInitials()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography 
                                variant="h3" 
                                sx={{ 
                                    color: 'white',
                                    fontWeight: 700,
                                    mb: 1,
                                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' }
                                }}
                            >
                                Welcome back, {user.firstName}!
                            </Typography>
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontWeight: 400,
                                    fontSize: '1.1rem'
                                }}
                            >
                                Account Profile
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => setShowEditModal(true)}
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                                color: 'white',
                                fontWeight: 600,
                                py: 1.5,
                                px: 3,
                                borderRadius: 3,
                                textTransform: 'none',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                                }
                            }}
                        >
                            Edit Profile
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Grid container spacing={4}>
                {/* Personal Information */}
                <Grid item xs={12} md={6}>
                    <Card 
                        elevation={4}
                        sx={{ 
                            borderRadius: 3,
                            height: '100%',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)'
                            }
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <PersonIcon sx={{ color: '#007A03', mr: 1, fontSize: '1.8rem' }} />
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        fontWeight: 700, 
                                        color: '#2c3e50',
                                        fontSize: '1.4rem'
                                    }}
                                >
                                    Personal Information
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />
                            
                            <InfoItem 
                                icon={<EmailIcon />} 
                                label="Email" 
                                value={user.username}
                                span
                            />
                            <InfoItem 
                                icon={<PersonIcon />} 
                                label="First Name" 
                                value={user.firstName}
                            />
                            <InfoItem 
                                icon={<PersonIcon />} 
                                label="Last Name" 
                                value={user.lastName}
                            />
                            <InfoItem 
                                icon={<PhoneIcon />} 
                                label="Phone" 
                                value={user.phone}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Address Information */}
                <Grid item xs={12} md={6}>
                    <Card 
                        elevation={4}
                        sx={{ 
                            borderRadius: 3,
                            height: '100%',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)'
                            }
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <HomeIcon sx={{ color: '#007A03', mr: 1, fontSize: '1.8rem' }} />
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        fontWeight: 700, 
                                        color: '#2c3e50',
                                        fontSize: '1.4rem'
                                    }}
                                >
                                    Address Information
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 3 }} />
                            
                            <InfoItem 
                                icon={<HomeIcon />} 
                                label="Address 1" 
                                value={user.address1}
                            />
                            <InfoItem 
                                icon={<HomeIcon />} 
                                label="Address 2" 
                                value={user.address2}
                            />
                            <InfoItem 
                                icon={<CityIcon />} 
                                label="City" 
                                value={user.city}
                            />
                            <InfoItem 
                                icon={<CityIcon />} 
                                label="State" 
                                value={user.state}
                            />
                            <InfoItem 
                                icon={<CityIcon />} 
                                label="ZIP Code" 
                                value={user.zip}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* License Information */}
                <Grid item xs={12}>
                    <Card 
                        elevation={4}
                        sx={{ 
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)'
                            }
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <LicenseIcon sx={{ color: '#007A03', mr: 1, fontSize: '1.8rem' }} />
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        fontWeight: 700, 
                                        color: '#2c3e50',
                                        fontSize: '1.4rem'
                                    }}
                                >
                                    License Information
                                </Typography>
                                <Chip 
                                    label="Sensitive Information" 
                                    size="small" 
                                    sx={{ 
                                        ml: 2,
                                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                        color: '#f57c00',
                                        fontSize: '0.75rem'
                                    }} 
                                />
                            </Box>
                            <Divider sx={{ mb: 3 }} />
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <InfoItem 
                                        icon={<LicenseIcon />} 
                                        label="License Number" 
                                        value={user.driversLicenseNumber}
                                        span
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InfoItem 
                                        icon={<CityIcon />} 
                                        label="Issued State" 
                                        value={user.licenseState}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <EditUserModal
                onClose={() => setShowEditModal(false)}
                userInfo={user}
                open={showEditModal}
                userApiService={userApiService}
                onCancel={() => setShowEditModal(false)}
                setSnackbarProperties={setSnackbarProperties}
            />
        </Container>
    );
}

UserInfo.propTypes = {
    setSnackbarProperties: PropTypes.func,
};
